import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import {
  Button,
  DataTable,
  FullPageStatus,
  Input,
  Modal,
  showToast,
  type DataTableColumn,
} from "@vv/ui";
import { useEffect, useState, type FormEvent } from "react";
import { useParams } from "react-router-dom";
import { ALL_TEAM_ROLES, TEAM_ROLE_LABELS, TeamRole, type TeamMember } from "../domain/team.types";
import { useTeamsDispatch, useTeamsSelector } from "../state/hooks";
import {
  addTeamMember,
  fetchTeamMembers,
  fetchTeams,
  removeTeamMember,
} from "../state/teams.thunks";

export interface TeamDetailPageProps {
  isOwner: boolean;
}

export function TeamDetailPage({ isOwner }: TeamDetailPageProps) {
  const { teamId } = useParams<{ teamId: string }>();
  const dispatch = useTeamsDispatch();

  const team = useTeamsSelector((state) => state.teams.teams.find((t) => t.id === teamId));
  const teamsStatus = useTeamsSelector((state) => state.teams.status);
  const members =
    useTeamsSelector((state) => (teamId ? state.teams.members[teamId] : undefined)) ?? [];
  const membersStatus = useTeamsSelector((state) => state.teams.membersStatus);

  const [addOpen, setAddOpen] = useState(false);
  const [newUserId, setNewUserId] = useState("");
  const [newRole, setNewRole] = useState<TeamRole>(TeamRole.Requestor);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!team && teamsStatus === "idle") {
      dispatch(fetchTeams());
    }
  }, [dispatch, team, teamsStatus]);

  useEffect(() => {
    if (teamId) {
      dispatch(fetchTeamMembers(teamId));
    }
  }, [dispatch, teamId]);

  async function handleAddMember(event: FormEvent) {
    event.preventDefault();
    if (!teamId) return;
    setSaving(true);
    try {
      await dispatch(
        addTeamMember({ teamId, payload: { userId: newUserId.trim(), role: newRole } }),
      ).unwrap();
      showToast("Member added", "success");
      setAddOpen(false);
      setNewUserId("");
      setNewRole(TeamRole.Requestor);
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Could not add member", "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleRemoveMember(member: TeamMember) {
    if (!teamId) return;
    try {
      await dispatch(
        removeTeamMember({ teamId, userId: member.userId, role: member.role }),
      ).unwrap();
      showToast("Member removed", "success");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Could not remove member", "error");
    }
  }

  const columns: DataTableColumn<TeamMember>[] = [
    { key: "userId", header: "User ID", render: (member) => member.userId },
    {
      key: "role",
      header: "Role",
      render: (member) => <Chip label={TEAM_ROLE_LABELS[member.role]} size="small" />,
    },
    {
      key: "actions",
      header: "",
      align: "right",
      width: 64,
      render: (member) =>
        isOwner ? (
          <Tooltip title="Remove role">
            <IconButton
              size="small"
              aria-label="remove member role"
              onClick={() => handleRemoveMember(member)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null,
    },
  ];

  if (!team) {
    return <FullPageStatus variant="loading" title="Loading team..." />;
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {team.name}
        </Typography>
        {isOwner ? (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
            Add member
          </Button>
        ) : null}
      </Stack>
      {team.description ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {team.description}
        </Typography>
      ) : null}

      <DataTable<TeamMember>
        columns={columns}
        rows={members}
        getRowId={(member) => member.id}
        loading={membersStatus === "loading"}
        emptyMessage="No members yet."
      />

      <Modal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title="Add team member"
        actions={
          <>
            <Button onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button
              type="submit"
              form="add-member-form"
              variant="contained"
              loading={saving}
              disabled={newUserId.trim().length === 0}
            >
              Add
            </Button>
          </>
        }
      >
        <Stack
          component="form"
          id="add-member-form"
          spacing={2}
          onSubmit={handleAddMember}
          sx={{ pt: 1 }}
        >
          <Input
            label="User ID"
            value={newUserId}
            onChange={(event) => setNewUserId(event.target.value)}
            required
            autoFocus
            helperText="Paste the user's id - there's no user directory yet"
          />
          <Input
            select
            label="Role"
            value={newRole}
            onChange={(event) => setNewRole(event.target.value as TeamRole)}
          >
            {ALL_TEAM_ROLES.map((role) => (
              <MenuItem key={role} value={role}>
                {TEAM_ROLE_LABELS[role]}
              </MenuItem>
            ))}
          </Input>
        </Stack>
      </Modal>
    </Box>
  );
}
